/**
 * Preferred-contact-method choices on Coach records.
 *
 * `Quo` is the legacy connector enum value for phone outreach (see `ConnectorSettingsPage.tsx`
 * and `ContactDetailView.tsx`, which both carry the same "Quo" -> "Phone" translation) — the UI
 * has always shown it as "Phone" while the wire value stays "Quo". Values mirror the API field
 * exactly; keep the two in sync.
 */
export const PREFERRED_CONTACT_METHODS = [
  { value: "Gmail", label: "Gmail" },
  { value: "Quo", label: "Phone" },
  { value: "X", label: "X" },
] as const;

export type PreferredContactMethodValue = (typeof PREFERRED_CONTACT_METHODS)[number]["value"];

/** Human label for a value; falls back to the raw value for anything unrecognized. */
export const preferredContactMethodLabel = (value: string | undefined | null): string => {
  if (!value) return "";
  return PREFERRED_CONTACT_METHODS.find((m) => m.value === value)?.label ?? value;
};

/** Inverse of {@link preferredContactMethodLabel}; undefined for an unrecognized label. */
export const preferredContactMethodValue = (label: string): PreferredContactMethodValue | undefined =>
  PREFERRED_CONTACT_METHODS.find((m) => m.label === label)?.value;

/**
 * Labels for the methods that are actually usable right now, in the same order as
 * `PREFERRED_CONTACT_METHODS`.
 *
 * The native `<select>` this replaces rendered every method always, disabling the ones with no
 * backing contact info (`<option value="Quo" disabled={!phone}>`). `SelectPopover` has no
 * per-option disabled state, so the equivalent here is to leave an unusable method out of the
 * menu entirely rather than let the coach pick a contact method with nothing behind it.
 */
export const availablePreferredContactMethodLabels = (
  email: string | undefined | null,
  phone: string | undefined | null,
  xHandle: string | undefined | null
): string[] =>
  PREFERRED_CONTACT_METHODS.filter((m) => {
    if (m.value === "Gmail") return Boolean(email);
    if (m.value === "Quo") return Boolean(phone);
    return Boolean(xHandle); // "X"
  }).map((m) => m.label);
